// src/aiFormat.js
// Turns the AI Analyst's Markdown-style reply into React elements.
//
// Safety: everything is rendered as React text nodes. There is no
// dangerouslySetInnerHTML and no HTML strings, so text from the owner's data
// (vendor or client names) can never run as markup. Links are plain text.
//
// Shape: parseAnswer(text) -> list of blocks -> AIAnswer renders each block.
// To add a new block type later (for example "chart"):
//   1. add a parser to FENCE_PARSERS (key = the word after the ``` fence),
//   2. add a renderer to RENDERERS (key = the block type it returns).
// Nothing else needs to change.

import { Fragment, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { parseChartSpec, buildChart, chartTable, formatMoney, formatPercent, formatAxisMoney, CHART_FAILED_NOTE } from "./aiCharts";

// The same sentence as CPA_SENTENCE in api/_aiRules.js. The build cannot import
// from api/ into src/, so it is copied here and a test asserts the two match.
export const CPA_SENTENCE = "Consult a CPA or tax attorney before making any financial decisions.";

// Same palette as the top of src/App.js (kept in step by hand).
const CARD = "#FEFDFB";
const BORDER = "#D4CCBC";
const DIM = "#9C8A74";
const MID = "#6B5E4E";
const DARK = "#2C2416";
const AMBER = "#C49020";
const GREEN = "#2D5E30"; // ACCENT2 in App.js: profit and the Send button
const GRID = "#E4DED2"; // BG2 in App.js

// ─── Small helpers ───────────────────────────────────────────────────────────

const stripBold = (s) => String(s).replace(/\*\*/g, "").trim();

// "**x** and y" -> ["", <strong>x</strong>, " and y"] style parts. Unmatched ** stay as text.
function inline(text, keyPrefix = "i") {
  const s = String(text == null ? "" : text);
  const parts = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(...italics(s.slice(last, m.index), `${keyPrefix}t${n}`));
    parts.push(<strong key={`${keyPrefix}${n++}`} style={{ fontWeight: 600, color: DARK }}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(...italics(s.slice(last), `${keyPrefix}t${n}`));
  return parts;
}

// *text* -> <em>, only when the * touches non-space text and is not next to a word
// character or another *. So "5 * 3 * 2", a lone * and "a*b*c" stay as typed.
function italics(s, keyPrefix) {
  const out = [];
  const re = /(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = re.exec(s)) !== null) {
    const start = m.index + m[1].length;
    if (start > last) out.push(s.slice(last, start));
    out.push(<em key={`${keyPrefix}e${n++}`}>{m[2]}</em>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

// Money, percentages and plain numbers, including negatives and a leading ~.
// Examples: 12, 1,234.50, $8,000, -$8,000, $-8,000, ~$495,503, 12.5%, (1,200), 4k
const NUMERIC_RE = /^[+~]?\(?[-−–+]?~?\$?[-−–+]?\d[\d,]*(\.\d+)?[%kKmMx]?\)?$/;
export function isNumericCell(text) {
  const t = stripBold(text == null ? "" : text);
  return t !== "" && NUMERIC_RE.test(t);
}

const PLACEHOLDER_RE = /^(-|—|–|n\/a|na|none)?$/i;

function splitRow(line) {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

function isSeparatorLine(line) {
  if (!line || line.indexOf("|") === -1) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c));
}

const HR_RE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const DATA_CHECK_RE = /^(\*\*)?\s*Data check(?::(\*\*)?|(\*\*):)\s*(.*)$/i;

function isCpaLine(line) {
  const t = line.replace(/\*\*|__|[*_"“”>]/g, "").trim();
  return t === CPA_SENTENCE;
}

function parseDataCheck(line) {
  const m = line.trim().match(DATA_CHECK_RE);
  if (!m) return null;
  let rest = m[4] || "";
  if (m[1] && !m[2] && !m[3]) rest = rest.replace(/\*\*\s*$/, ""); // "**Data check: text**"
  return { type: "datacheck", text: rest.trim() };
}

// ─── Fenced blocks ───────────────────────────────────────────────────────────

// "Label | value" lines. Returns null when nothing usable so the caller falls back to text.
function parseFigures(bodyLines) {
  const items = [];
  for (const raw of bodyLines) {
    const i = raw.indexOf("|");
    if (i === -1) continue;
    const label = stripBold(raw.slice(0, i));
    const value = stripBold(raw.slice(i + 1));
    if (label && value) items.push({ label, value });
  }
  return items.length ? { type: "figures", items } : null;
}

// The chart block only carries the AI's choice of what to draw. The numbers come
// from the owner's data when it is rendered. A bad spec never falls back to the raw text.
function parseChart(bodyLines) {
  return { type: "chart", spec: parseChartSpec(bodyLines.join("\n")) };
}

const FENCE_PARSERS = {
  figures: parseFigures,
  chart: parseChart,
};

// ─── Block parser ────────────────────────────────────────────────────────────

export function parseAnswer(input) {
  const text = typeof input === "string" ? input : input == null ? "" : String(input);
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let para = [];

  const flushPara = () => {
    if (para.length) blocks.push({ type: "paragraph", text: para.join("\n") });
    para = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") { flushPara(); i++; continue; }

    // Fenced block
    if (trimmed.startsWith("```")) {
      flushPara();
      const lang = trimmed.slice(3).trim().toLowerCase();
      const body = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) { body.push(lines[i]); i++; }
      i++; // skip closing fence (or run off the end if it was never closed)
      const parser = FENCE_PARSERS[lang];
      let block = null;
      if (parser) {
        try { block = parser(body); } catch (e) { block = null; }
      }
      blocks.push(block || { type: "text", text: body.join("\n") });
      continue;
    }

    // Data check line
    const dc = parseDataCheck(line);
    if (dc) { flushPara(); blocks.push(dc); i++; continue; }

    // CPA sentence
    if (isCpaLine(line)) { flushPara(); blocks.push({ type: "cpa", text: CPA_SENTENCE }); i++; continue; }

    // Horizontal rule
    if (HR_RE.test(line)) { flushPara(); blocks.push({ type: "rule" }); i++; continue; }

    // Heading
    const h = line.match(HEADING_RE);
    if (h) { flushPara(); blocks.push({ type: "heading", level: h[1].length, text: h[2] }); i++; continue; }

    // Table: a row with a pipe followed by a separator row
    if (line.indexOf("|") !== -1 && isSeparatorLine(lines[i + 1])) {
      flushPara();
      const header = splitRow(line);
      const sepCells = splitRow(lines[i + 1]);
      const rawLines = [line, lines[i + 1]];
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim() !== "" && lines[j].indexOf("|") !== -1) {
        rows.push(splitRow(lines[j]));
        rawLines.push(lines[j]);
        j++;
      }
      const wellFormed = sepCells.length === header.length && rows.every((r) => r.length <= header.length);
      if (wellFormed) {
        const padded = rows.map((r) => r.concat(Array(header.length - r.length).fill("")));
        blocks.push({ type: "table", header, rows: padded });
      } else {
        blocks.push({ type: "text", text: rawLines.join("\n") });
      }
      i = j;
      continue;
    }

    // Lists (an indented bullet under an item is kept with that item as a sub-item)
    const bm = line.match(BULLET_RE);
    const om = !bm && line.match(ORDERED_RE);
    if (bm || om) {
      flushPara();
      const ordered = !!om;
      const start = ordered ? parseInt(om[1], 10) : 1;
      const items = [];
      // A tab counts as 4 spaces. Nesting is measured from the first item's indent,
      // so a list that is indented as a whole does not nest under its first item.
      const indentOf = (l) => l.match(/^[ \t]*/)[0].replace(/\t/g, "    ").length;
      const baseIndent = indentOf(line);
      const classify = (l) => {
        const b2 = l.match(BULLET_RE);
        if (b2) return indentOf(l) >= baseIndent + 2 && items.length ? { sub: b2[1] } : ordered ? null : { item: b2[1] };
        const o2 = ordered && l.match(ORDERED_RE);
        return o2 ? { item: o2[2] } : null;
      };
      while (i < lines.length) {
        let k = i;
        while (k < lines.length && lines[k].trim() === "") k++; // blank lines between items
        if (k >= lines.length) break;
        const c = classify(lines[k]);
        if (!c) break;
        if (c.sub !== undefined) items[items.length - 1].subs.push(c.sub);
        else items.push({ text: c.item, subs: [] });
        i = k + 1;
      }
      blocks.push({ type: "list", ordered, start, items });
      continue;
    }

    para.push(line);
    i++;
  }
  flushPara();
  return blocks;
}

// ─── Renderers ───────────────────────────────────────────────────────────────

const blockGap = { margin: "0 0 10px" };

const HEADING_STYLES = {
  1: { fontSize: 16, fontWeight: 600 },
  2: { fontSize: 15, fontWeight: 600 },
  3: { fontSize: 13.5, fontWeight: 600 },
};

function Heading({ block }) {
  const s = HEADING_STYLES[Math.min(block.level, 3)];
  return <div style={{ ...s, color: DARK, margin: "14px 0 6px", lineHeight: 1.4 }}>{inline(block.text)}</div>;
}

function Paragraph({ block }) {
  return <p style={{ ...blockGap, whiteSpace: "pre-wrap" }}>{inline(block.text)}</p>;
}

function List({ block }) {
  const Tag = block.ordered ? "ol" : "ul";
  return (
    <Tag {...(block.ordered ? { start: block.start } : {})} style={{ ...blockGap, paddingLeft: 22 }}>
      {block.items.map((it, k) => (
        <li key={k} style={{ margin: "2px 0" }}>
          {inline(it.text)}
          {it.subs.length > 0 && (
            <ul style={{ margin: "2px 0 0", paddingLeft: 20 }}>
              {it.subs.map((sub, q) => <li key={q} style={{ margin: "2px 0" }}>{inline(sub, "s")}</li>)}
            </ul>
          )}
        </li>
      ))}
    </Tag>
  );
}

function Rule() {
  return <hr style={{ border: "none", borderTop: `1px solid ${BORDER}`, margin: "12px 0" }} />;
}

function DataCheck({ block }) {
  return (
    <div role="note" style={{ margin: "0 0 12px", padding: "8px 12px", borderLeft: `3px solid ${AMBER}`, background: "rgba(140,107,48,0.07)", borderRadius: 3, fontSize: 12, color: MID, lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, color: AMBER }}>Data check:</span>{" "}
      {inline(block.text)}
    </div>
  );
}

function CpaFooter({ block }) {
  return (
    <div style={{ margin: "12px 0 10px", paddingTop: 8, borderTop: `1px solid ${BORDER}`, fontSize: 11, color: DIM, lineHeight: 1.5 }}>{block.text}</div>
  );
}

function Figures({ block }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 28px", margin: "4px 0 14px", padding: "10px 0" }}>
      {block.items.map((it, k) => (
        <div key={k} style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: DIM, lineHeight: 1.4 }}>{it.label}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: DARK, fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ block }) {
  const colNumeric = block.header.map((_, c) => {
    const vals = block.rows.map((r) => stripBold(r[c])).filter((v) => !PLACEHOLDER_RE.test(v));
    return vals.length > 0 && vals.every(isNumericCell);
  });
  const alignFor = (c, text) => (colNumeric[c] || isNumericCell(text) ? "right" : "left");
  const cellBase = { padding: "6px 10px", fontSize: 12.5, verticalAlign: "top" };
  return (
    <div style={{ overflowX: "auto", maxWidth: "100%", margin: "0 0 12px" }}>
      <table style={{ borderCollapse: "collapse", minWidth: "100%", fontFamily: "'DM Sans',sans-serif" }}>
        <thead>
          <tr>
            {block.header.map((h, c) => (
              <th key={c} style={{ ...cellBase, fontSize: 11, fontWeight: 500, color: MID, textAlign: colNumeric[c] ? "right" : "left", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>{inline(h, "h")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 1 ? "rgba(228,222,210,0.45)" : CARD }}>
              {r.map((cell, c) => {
                const align = alignFor(c, cell);
                return (
                  <td key={c} style={{ ...cellBase, color: MID, textAlign: align, fontVariantNumeric: align === "right" ? "tabular-nums" : undefined, whiteSpace: align === "right" ? "nowrap" : undefined }}>{inline(cell, "c")}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlainText({ block }) {
  return <div style={{ ...blockGap, whiteSpace: "pre-wrap" }}>{block.text}</div>;
}

// ─── Chart ───────────────────────────────────────────────────────────────────

const smallLink = { cursor: "pointer", background: "none", border: "none", padding: 0, color: MID, fontSize: 12, fontFamily: "'DM Sans',sans-serif", textDecoration: "underline" };

function ChartTip({ active, payload, unit }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  if (!row || row.value === null || row.value === undefined) return null;
  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "6px 10px", fontSize: 12, color: DARK, boxShadow: "0 2px 8px rgba(44,36,22,0.12)" }}>
      <div style={{ color: MID }}>{row.fullLabel}</div>
      <div style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{unit === "percent" ? formatPercent(row.value) : formatMoney(row.value)}</div>
    </div>
  );
}

const truncate = (s, n) => (String(s).length > n ? String(s).slice(0, n - 1) + "\u2026" : String(s));

// Horizontal bar with only the outer end rounded (the right end, or the left end for a negative value).
function EndRoundedBar({ x, y, width, height }) {
  const w = Math.abs(width || 0);
  const h = Math.abs(height || 0);
  if (!w || !h) return null;
  const left = Math.min(x, x + (width || 0));
  const r = Math.min(3, w, h / 2);
  const neg = (width || 0) < 0;
  const d = neg
    ? `M${left + w},${y} H${left + r} Q${left},${y} ${left},${y + r} V${y + h - r} Q${left},${y + h} ${left + r},${y + h} H${left + w} Z`
    : `M${left},${y} H${left + w - r} Q${left + w},${y} ${left + w},${y + r} V${y + h - r} Q${left + w},${y + h} ${left + w - r},${y + h} H${left} Z`;
  return <path d={d} fill={GREEN} />;
}

function ChartGraphic({ chart }) {
  const { rows, unit, spec } = chart;
  const tick = { fontSize: 11, fill: MID, fontFamily: "'DM Sans',sans-serif" };
  const fmtTick = (v) => (unit === "percent" ? `${v}%` : formatAxisMoney(v));
  const horizontal = chart.orientation === "rows";
  const common = { data: rows, margin: { top: 8, right: 12, left: 0, bottom: 0 } };
  const tip = <Tooltip content={<ChartTip unit={unit} />} cursor={{ fill: "rgba(45,94,48,0.06)" }} />;
  // Wide enough for the longest axis label (about 7px a character), within sensible limits.
  const longest = Math.max(0, ...rows.map((r) => (r.value === null ? 0 : fmtTick(r.value).length)));
  const yWidth = Math.min(84, Math.max(48, longest * 7 + 12));
  const height = horizontal ? Math.max(240, rows.length * 30 + 50) : 240;
  let graph;
  if (horizontal) {
    graph = (
      <BarChart {...common} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" tickFormatter={fmtTick} tick={tick} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="label" width={110} tick={tick} tickFormatter={(v) => truncate(v, 16)} axisLine={false} tickLine={false} interval={0} />
        <ReferenceLine x={0} stroke={BORDER} />
        {tip}
        <Bar dataKey="value" fill={GREEN} isAnimationActive={false} shape={<EndRoundedBar />} />
      </BarChart>
    );
  } else if (spec.type === "line") {
    graph = (
      <LineChart {...common}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={tick} axisLine={{ stroke: BORDER }} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
        <YAxis tickFormatter={fmtTick} tick={tick} axisLine={false} tickLine={false} width={yWidth} />
        <ReferenceLine y={0} stroke={BORDER} />
        {tip}
        <Line type="monotone" dataKey="value" stroke={GREEN} strokeWidth={2} dot={{ r: 3, fill: GREEN, stroke: GREEN }} connectNulls={false} isAnimationActive={false} />
      </LineChart>
    );
  } else {
    graph = (
      <BarChart {...common}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={tick} axisLine={{ stroke: BORDER }} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
        <YAxis tickFormatter={fmtTick} tick={tick} axisLine={false} tickLine={false} width={yWidth} />
        <ReferenceLine y={0} stroke={BORDER} />
        {tip}
        <Bar dataKey="value" fill={GREEN} radius={[3, 3, 0, 0]} isAnimationActive={false} />
      </BarChart>
    );
  }
  return (
    <div role="img" aria-label={chart.ariaLabel} style={{ width: "100%", height, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">{graph}</ResponsiveContainer>
    </div>
  );
}

function ChartBlock({ block, jobs }) {
  const [asTable, setAsTable] = useState(false);
  let chart = { ok: false };
  try { chart = block.spec ? buildChart(block.spec, jobs) : { ok: false }; } catch (e) { chart = { ok: false }; }
  if (!chart.ok) {
    return <div role="note" style={{ ...blockGap, fontSize: 12, color: DIM }}>{CHART_FAILED_NOTE}</div>;
  }
  return (
    <div style={{ margin: "4px 0 14px", padding: "12px 14px", border: `1px solid ${BORDER}`, borderRadius: 6, background: CARD }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "2px 12px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: DARK, lineHeight: 1.4, minWidth: 0 }}>{chart.title}</div>
        <div style={{ fontSize: 11, color: DIM, marginLeft: "auto" }}>Drawn from your Canopy data</div>
      </div>
      <div style={{ textAlign: "right", margin: "2px 0 6px" }}>
        <button type="button" onClick={() => setAsTable((v) => !v)} style={smallLink}>{asTable ? "Show as chart" : "Show as table"}</button>
      </div>
      {asTable ? <Table block={chartTable(chart)} /> : <ChartGraphic chart={chart} />}
      {chart.notes.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: DIM, lineHeight: 1.5 }}>{chart.notes.join(" ")}</div>
      )}
    </div>
  );
}

// Add future renderers here, keyed by block type.
const RENDERERS = {
  heading: Heading,
  paragraph: Paragraph,
  list: List,
  rule: Rule,
  datacheck: DataCheck,
  cpa: CpaFooter,
  figures: Figures,
  table: Table,
  chart: ChartBlock,
  text: PlainText,
};

// jobs: the owner's job summaries, used to fill in chart blocks (never numbers from the AI's text).
export function AIAnswer({ text, jobs }) {
  let blocks;
  try {
    blocks = parseAnswer(text);
  } catch (e) {
    blocks = null;
  }
  if (!blocks || blocks.length === 0) {
    return <div style={{ whiteSpace: "pre-wrap" }}>{typeof text === "string" ? text : ""}</div>;
  }
  return (
    <Fragment>
      {blocks.map((b, k) => {
        const R = RENDERERS[b.type] || PlainText;
        return <R key={k} block={b.type in RENDERERS ? b : { text: b.text || "" }} jobs={jobs} />;
      })}
    </Fragment>
  );
}
